import {
    pitchTypeCategories,
    pitchTypes,
} from 'bam-node-baseball-savant/apps/utils/possible-comp-tool-stats.ts'
import getShortPitchName from 'bam-node-baseball-savant/utils/get-short-pitch-name.ts'
import { StatGroup, PlayerRow } from './types.ts'

export const BATTING_STATS = new Set([
    'percent_rank_xwoba',
    'percent_rank_xera',
    'percent_rank_woba',
    'percent_rank_xwobacon',
    'percent_rank_wobacon',
    'percent_rank_xba',
    'percent_rank_ba',
    'percent_rank_xobp',
    'percent_rank_obp',
    'percent_rank_xslg',
    'percent_rank_slg',
    'percent_rank_bacon',
    'percent_rank_xbacon',
    'percent_rank_babip',
    'percent_rank_xiso',
    'percent_rank_iso',
    'percent_rank_exit_velocity_avg',
    'percent_rank_exit_velocity_max',
    'percent_rank_distance_hr_avg',
    'percent_rank_xhr',
    'percent_rank_barrel',
    'percent_rank_barrel_batted_rate',
    'percent_rank_launch_angle_avg',
    'percent_rank_hard_hit_percent',
    'percent_rank_sweet_spot_percent',
    'percent_rank_swing_speed',
    'percent_rank_swing_length',
    'percent_rank_squared_up_swing',
    'percent_rank_blasts_swing',
    'percent_rank_chase_percent',
    'percent_rank_whiff_percent',
    'percent_rank_k_percent',
    'percent_rank_bb_percent',
    'percent_rank_groundballs_percent',
    'percent_rank_airballs_percent',
    'percent_rank_pull_percent_airballs',
    'percent_rank_avg_hyper_speed',
    'percent_rank_avg_best_speed',
    'percent_rank_attack_angle',
    'percent_rank_vertical_swing_path',
    // 'percent_rank_acceleration',
    'percent_rank_ideal_angle_rate',
])

export const RUNNING_STATS = new Set([
    'percent_rank_speed_order',
    'percent_rank_runner_runs_sb',
    'percent_rank_runner_runs_xb',
])

export const CATCHING_STATS = new Set([
    'percent_rank_blocks_above_average',
    'percent_rank_cs_above_average',
    'percent_rank_fielding_run_value_framing',
    'percent_rank_framing',
    'percent_rank_pop_2b',
    'percent_rank_arm_cs_2b',
])

export const FIELDING_STATS = new Set([
    'percent_rank_oaa',
    'percent_rank_fielding_run_value_arm',
    'percent_rank_arm_overall',
    'percent_rank_arm_max',
    'percent_rank_jump',
])

export const VALUE_STATS = new Set([
    'percent_rank_swing_take_run_value',
    'percent_rank_fielding_run_value',
    'percent_rank_runner_run_value',
    'percent_rank_pitch_run_value_fastball',
    'percent_rank_pitch_run_value_breaking',
    'percent_rank_pitch_run_value_offspeed',
])

export const PITCH_ARSENAL_STATS = new Set(
    pitchTypes.flatMap((pitch) =>
        pitchTypeCategories.map(
            (category) => `${pitch}_percent_rank_${category}`
        )
    )
)

export const PITCH_ARSENAL_BATTER_STATS = new Set(
    [...PITCH_ARSENAL_STATS].filter(
        (d) =>
            d.includes('pitch_run_value') ||
            d.includes('pitch_usage') ||
            d.includes('pitch_whiff_percent')
    )
)

export function groupStatSelects(
    selects: (keyof PlayerRow)[],
    currType: 'batter' | 'pitcher' = 'batter'
) {
    const groupedStats: Record<StatGroup, (keyof PlayerRow)[]> = {
        value: [],
        batting: [],
        pitching: [],
        fielding: [],
        catching: [],
        running: [],
        pitch_arsenal: [],
    }

    selects.forEach((stat) => {
        if (BATTING_STATS.has(stat)) {
            if (currType === 'batter') {
                if (stat !== 'percent_rank_xera') {
                    groupedStats.batting.push(stat)
                }
            } else if (stat !== 'percent_rank_xwoba') {
                groupedStats.pitching.push(stat)
            }
        } else if (RUNNING_STATS.has(stat)) {
            groupedStats.running.push(stat)
        } else if (CATCHING_STATS.has(stat)) {
            groupedStats.catching.push(stat)
        } else if (FIELDING_STATS.has(stat)) {
            groupedStats.fielding.push(stat)
        } else if (VALUE_STATS.has(stat)) {
            groupedStats.value.push(stat)
        } else if (
            (PITCH_ARSENAL_STATS.has(stat) && currType === 'pitcher') ||
            (PITCH_ARSENAL_BATTER_STATS.has(stat) && currType === 'batter')
        ) {
            groupedStats.pitch_arsenal.push(stat)
        }
    })

    return groupedStats
}

export const prettyPitchArsenalNames: Record<string, string> = {
    pitch_velo: 'Velocity',
    pitch_spin: 'Spin Rate',
    pitch_extension: 'Extension',
    pitch_run_value: 'Run Value',
    pitch_whiff_percent: 'Whiff %',
    pitch_break_x: 'Horiz. Break',
    pitch_break_z: 'Drop',
    pitch_break_z_induced: 'IVB',
    pitch_usage: 'Usage %',
}

export const mobilePrettyPitchArsenalNames: Record<string, string> = {
    pitch_velo: 'Velo.',
    pitch_spin: 'Spin',
    pitch_extension: 'Ext.',
    pitch_run_value: 'RV',
    pitch_whiff_percent: 'Whiff',
    pitch_break_x: 'HBreak',
    pitch_break_z: 'Drop',
    pitch_break_z_induced: 'IVB',
    pitch_usage: 'Usage',
}

const pitchArsenalCombinations = Object.fromEntries(
    pitchTypes.flatMap((pitch) =>
        pitchTypeCategories.map((category) => {
            const key = `${pitch}_percent_rank_${category}`
            const value = `${getShortPitchName(pitch)} ${
                prettyPitchArsenalNames[category]
            }`

            return [key, value]
        })
    )
)

const mobilePitchArsenalCombinations = Object.fromEntries(
    pitchTypes.flatMap((pitch) =>
        pitchTypeCategories.map((category) => {
            const key = `${pitch}_percent_rank_${category}`
            const value = `${getShortPitchName(pitch)} ${
                mobilePrettyPitchArsenalNames[category]
            }`

            return [key, value]
        })
    )
)

export const prettyStatNames: Record<string, string> = {
    percent_rank_barrel: 'Barrels',
    percent_rank_barrel_batted_rate: 'Barrel %',
    percent_rank_exit_velocity_avg: 'Avg. Exit Velo.',
    percent_rank_exit_velocity_max: 'Max. Exit Velo.',
    percent_rank_launch_angle_avg: 'Avg. Launch Angle',
    percent_rank_xba: 'xBA',
    percent_rank_xslg: 'xSLG',
    percent_rank_xwoba: 'xwOBA',
    percent_rank_woba: 'wOBA',
    percent_rank_hard_hit_percent: 'Hard-Hit %',
    percent_rank_xwobacon: 'xwOBAcon',
    percent_rank_wobacon: 'wOBAcon',
    percent_rank_k_percent: 'K %',
    percent_rank_bb_percent: 'BB %',
    percent_rank_whiff_percent: 'Whiff %',
    percent_rank_chase_percent: 'Chase %',
    percent_rank_ba: 'BA',
    percent_rank_bacon: 'BAcon',
    percent_rank_xbacon: 'xBAcon',
    percent_rank_babip: 'BABIP',
    percent_rank_obp: 'OBP',
    percent_rank_slg: 'SLG',
    percent_rank_xobp: 'xOBP',
    percent_rank_iso: 'ISO',
    percent_rank_xiso: 'xISO',
    percent_rank_sweet_spot_percent: 'LA Sweet Spot %',
    percent_rank_distance_hr_avg: 'Avg. HR Distance',
    percent_rank_groundballs_percent: 'GB %',
    percent_rank_airballs_percent: 'AIR %',
    percent_rank_pull_percent_airballs: 'Pull AIR %',
    percent_rank_avg_hyper_speed: 'Adjusted EV',
    percent_rank_avg_best_speed: 'EV50',
    percent_rank_pitch_run_value_fastball: 'Fastballs',
    percent_rank_pitch_run_value_breaking: 'Breaking',
    percent_rank_pitch_run_value_offspeed: 'Offspeed',
    percent_rank_arm_cs_2b: 'Catcher Arm Strength 2B',
    percent_rank_swing_speed: 'Bat Speed',
    percent_rank_swing_length: 'Swing Length',
    percent_rank_squared_up_swing: 'Squared-Up %',
    percent_rank_blasts_swing: 'Blast %',
    percent_rank_attack_angle: 'Attack Angle',
    percent_rank_vertical_swing_path: 'Swing Tilt',
    // percent_rank_acceleration: 'Bat Acceleration',
    percent_rank_speed_order: 'Sprint Speed',
    percent_rank_pop_2b: 'Catcher Pop 2B',
    percent_rank_framing: 'Catcher Framing',
    percent_rank_jump: 'Outfield Jump',
    percent_rank_oaa: 'Outs Above Average',
    percent_rank_arm_max: 'Max. Arm Strength',
    percent_rank_arm_overall: 'INF/OF Arm Strength',
    percent_rank_fastball_velo: 'Avg. FB Velo.',
    percent_rank_fastball_spin: 'Avg. FB Spin',
    percent_rank_fastball_extension: 'Avg. FB Extension',
    percent_rank_cu_spin: 'Avg. CU Spin',
    percent_rank_xera: 'xERA',
    percent_rank_xhr: 'xHR',
    percent_rank_swing_take_run_value: 'Batting',
    percent_rank_blocks_above_average: 'Blocks Above Avg',
    percent_rank_cs_above_average: 'CS Above Avg',
    percent_rank_fielding_run_value: 'Fielding',
    percent_rank_runner_run_value: 'Baserunning',
    percent_rank_runner_runs_sb: 'SB Runs',
    percent_rank_runner_runs_xb: 'XB Taken Runs',
    percent_rank_fielding_run_value_arm: 'OF Arm Value',
    percent_rank_fielding_run_value_framing: 'Catcher Framing',
    percent_rank_ideal_angle_rate: 'Ideal Attack Angle %',
    ...pitchArsenalCombinations,
}

export const prettyStatNamesMobile: Record<string, string> = {
    percent_rank_barrel: 'Barrels',
    percent_rank_barrel_batted_rate: 'Barrel %',
    percent_rank_exit_velocity_avg: 'Avg. EV',
    percent_rank_exit_velocity_max: 'Max. EV',
    percent_rank_launch_angle_avg: 'Avg. LA',
    percent_rank_xba: 'xBA',
    percent_rank_xslg: 'xSLG',
    percent_rank_xwoba: 'xwOBA',
    percent_rank_woba: 'wOBA',
    percent_rank_hard_hit_percent: 'Hard-Hit %',
    percent_rank_xwobacon: 'xwOBAcon',
    percent_rank_wobacon: 'wOBAcon',
    percent_rank_k_percent: 'K %',
    percent_rank_bb_percent: 'BB %',
    percent_rank_whiff_percent: 'Whiff %',
    percent_rank_chase_percent: 'Chase %',
    percent_rank_ba: 'BA',
    percent_rank_bacon: 'BAcon',
    percent_rank_xbacon: 'xBAcon',
    percent_rank_babip: 'BABIP',
    percent_rank_obp: 'OBP',
    percent_rank_slg: 'SLG',
    percent_rank_xobp: 'xOBP',
    percent_rank_iso: 'ISO',
    percent_rank_xiso: 'xISO',
    percent_rank_sweet_spot_percent: 'LA SwSp %',
    percent_rank_distance_hr_avg: 'Avg. HR Dist.',
    percent_rank_groundballs_percent: 'GB %',
    percent_rank_airballs_percent: 'AIR %',
    percent_rank_pull_percent_airballs: 'Pull AIR %',
    percent_rank_avg_hyper_speed: 'Adj. EV',
    percent_rank_avg_best_speed: 'EV50',
    percent_rank_pitch_run_value_fastball: 'Fastballs',
    percent_rank_pitch_run_value_breaking: 'Breaking',
    percent_rank_pitch_run_value_offspeed: 'Offspeed',
    percent_rank_arm_cs_2b: 'Arm Str. 2B',
    percent_rank_swing_speed: 'Bat Speed',
    percent_rank_swing_length: 'Swing Length',
    percent_rank_squared_up_swing: 'Squared-Up %',
    percent_rank_blasts_swing: 'Blast %',
    percent_rank_attack_angle: 'Attack Angle',
    percent_rank_vertical_swing_path: 'Swing Tilt',
    // percent_rank_acceleration: 'Bat Accel.',
    percent_rank_speed_order: 'Sprint Speed',
    percent_rank_pop_2b: 'Pop 2B',
    percent_rank_framing: 'Framing',
    percent_rank_jump: 'OF Jump',
    percent_rank_oaa: 'OAA',
    percent_rank_arm_max: 'Max. Arm Str.',
    percent_rank_arm_overall: 'INF/OF Arm Str.',
    percent_rank_fastball_velo: 'FB Velo.',
    percent_rank_fastball_spin: 'FB Spin',
    percent_rank_fastball_extension: 'FB Ext.',
    percent_rank_cu_spin: 'CU Spin',
    percent_rank_xera: 'xERA',
    percent_rank_xhr: 'xHR',
    percent_rank_swing_take_run_value: 'Batting',
    percent_rank_blocks_above_average: 'Blocks Above Avg.',
    percent_rank_cs_above_average: 'CS Above Avg.',
    percent_rank_fielding_run_value: 'Fielding',
    percent_rank_runner_run_value: 'Baserunning',
    percent_rank_runner_runs_sb: 'SB Runs',
    percent_rank_runner_runs_xb: 'XB Taken Runs',
    percent_rank_fielding_run_value_arm: 'OF Arm Value',
    percent_rank_fielding_run_value_framing: 'Framing',
    percent_rank_ideal_angle_rate: 'Ideal Att. Ang. %',
    ...mobilePitchArsenalCombinations,
}
